import { ref } from 'vue';

import { exhaustiveGuard } from '@libs/utils';

import type {
  AppTooltipConfig,
  AppTooltipOverflow,
  AppTooltipPosition,
  AppTriggerConfig,
} from './AppTooltip.model';

export function calculateTooltipPosition(
  triggerConfig: AppTriggerConfig,
  tooltipRef: HTMLElement | null,
  tooltipConfig: AppTooltipConfig,
  defaultPosition: AppTooltipPosition
): void {
  const margin = 8;
  if (!tooltipRef) {
    return;
  }
  const tooltipInitialWidth = tooltipRef.getBoundingClientRect().width;

  const MAX_TOOLTIP_WIDTH = 250;

  tooltipConfig.height = tooltipRef.getBoundingClientRect().height;

  const maxWidth =
    tooltipInitialWidth < MAX_TOOLTIP_WIDTH
      ? tooltipInitialWidth
      : MAX_TOOLTIP_WIDTH;

  tooltipConfig.width = maxWidth;

  const baseTop = triggerConfig.YPosition + triggerConfig.height + margin;
  const baseBottom = triggerConfig.YPosition - tooltipConfig.height - margin;
  const centerLeft =
    triggerConfig.XPosition - tooltipConfig.width / 2 + triggerConfig.width / 2;
  const halfHeightOffset =
    triggerConfig.YPosition -
    tooltipConfig.height / 2 +
    triggerConfig.height / 2;
  const leftAligned =
    triggerConfig.XPosition - tooltipConfig.width + triggerConfig.width;

  switch (defaultPosition) {
    case 'bottom':
      tooltipConfig.topPosition = `${baseTop}px`;
      tooltipConfig.leftPosition = `${centerLeft}px`;
      break;

    case 'bottom-left':
      tooltipConfig.topPosition = `${baseTop}px`;
      tooltipConfig.leftPosition = `${leftAligned}px`;
      break;

    case 'bottom-right':
      tooltipConfig.topPosition = `${baseTop}px`;
      tooltipConfig.leftPosition = `${triggerConfig.XPosition}px`;
      break;

    case 'top':
      tooltipConfig.topPosition = `${baseBottom}px`;
      tooltipConfig.leftPosition = `${centerLeft}px`;
      break;

    case 'top-left':
      tooltipConfig.topPosition = `${baseBottom}px`;
      tooltipConfig.leftPosition = `${leftAligned}px`;
      break;

    case 'top-right':
      tooltipConfig.topPosition = `${baseBottom}px`;
      tooltipConfig.leftPosition = `${triggerConfig.XPosition}px`;
      break;

    case 'right':
      tooltipConfig.topPosition = `${halfHeightOffset}px`;
      tooltipConfig.leftPosition = `${
        triggerConfig.XPosition + triggerConfig.width + margin
      }px`;
      break;

    case 'left':
      tooltipConfig.topPosition = `${halfHeightOffset}px`;
      tooltipConfig.leftPosition = `${
        triggerConfig.XPosition - tooltipConfig.width - margin
      }px`;
      break;

    default:
      exhaustiveGuard(
        defaultPosition,
        'Unhandled position value in AppTooltip component'
      );
  }
}

export function checkOverflowAndAdjustPosition(
  tooltipHeight: number,
  tooltipWidth: number,
  prevPosition: AppTooltipPosition,
  triggerConfig: AppTriggerConfig
): AppTooltipPosition | undefined {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const margin = 25;

  const tooltipOverflow = ref<AppTooltipOverflow>({
    top: triggerConfig.YPosition - tooltipHeight - margin < 0,
    bottom: triggerConfig.YPosition + tooltipHeight + margin > viewportHeight,
    right: triggerConfig.XPosition + tooltipWidth + margin > viewportWidth,
    left: triggerConfig.XPosition - (tooltipWidth + margin) < 0,
  });
  const isTooltipOverflowingOnAllSides = Object.values(
    tooltipOverflow.value
  ).every(Boolean);
  if (isTooltipOverflowingOnAllSides) {
    return;
  }

  const isTooltipNotOverflowing = Object.values(tooltipOverflow.value).every(
    (position) => !position
  );
  if (isTooltipNotOverflowing) {
    return prevPosition;
  }

  if (
    tooltipOverflow.value.right &&
    tooltipOverflow.value.left &&
    tooltipOverflow.value.top
  ) {
    return 'bottom';
  }
  if (
    tooltipOverflow.value.right &&
    tooltipOverflow.value.left &&
    tooltipOverflow.value.bottom
  ) {
    return 'top';
  }
  if (
    tooltipOverflow.value.top &&
    tooltipOverflow.value.bottom &&
    tooltipOverflow.value.left
  ) {
    return 'right';
  }
  if (
    tooltipOverflow.value.top &&
    tooltipOverflow.value.bottom &&
    tooltipOverflow.value.right
  ) {
    return 'left';
  }

  if (tooltipOverflow.value.top && tooltipOverflow.value.right) {
    return 'bottom-left';
  }
  if (tooltipOverflow.value.top && tooltipOverflow.value.left) {
    return 'bottom-right';
  }
  if (tooltipOverflow.value.bottom && tooltipOverflow.value.right) {
    return 'top-left';
  }
  if (tooltipOverflow.value.bottom && tooltipOverflow.value.left) {
    return 'top-right';
  }
  if (tooltipOverflow.value.right && tooltipOverflow.value.left) {
    return 'bottom';
  }
  if (tooltipOverflow.value.top && tooltipOverflow.value.bottom) {
    return 'right';
  }
  if (tooltipOverflow.value.top) {
    return 'bottom';
  }
  if (tooltipOverflow.value.bottom) {
    return 'top';
  }
  if (tooltipOverflow.value.right) {
    return 'left';
  }
  if (tooltipOverflow.value.left) {
    return 'right';
  }
}

export function adjustTooltipPosition(
  tooltipRef: HTMLElement,
  defaultPosition: AppTooltipPosition,
  triggerConfig: AppTriggerConfig,
  tooltipConfig: AppTooltipConfig
): AppTooltipPosition {
  const newPosition = checkOverflowAndAdjustPosition(
    tooltipRef.getBoundingClientRect().height,
    tooltipRef.getBoundingClientRect().width,
    defaultPosition,
    triggerConfig
  );

  if (newPosition && newPosition !== defaultPosition) {
    const updatedPosition = newPosition;

    calculateTooltipPosition(
      triggerConfig,
      tooltipRef,
      tooltipConfig,
      updatedPosition
    );

    return updatedPosition;
  }

  calculateTooltipPosition(
    triggerConfig,
    tooltipRef,
    tooltipConfig,
    defaultPosition
  );

  return defaultPosition;
}
